import * as React from "react";
import cx from "classnames";

import {
  Field,
} from "@modules/fhq-design-tool/components/questionnaire-management/components/fields/component/field";

import {
  DraggableList,
  DraggableListProps,
} from "@common/components/draggable-list";

import "./fields-with-text-blocks.styles.scss";
import {
    FieldInList,
  FieldsWithTextBlocksItem,
  FieldsWithTextBlocksItemType,
  FieldsWithTextBlocksProps,
  UpdatedLocalFields,
  UpdatedLocalTexts,
} from "./fields-with-text-blocks.types";

import {
  TextBlock,
  TextBlockItem,
} from "../questionnaire-designer/components/text-blocks/component/item";

import "./fields-with-text-blocks.styles.scss";

export function FieldsWithTextBlocks<F extends FieldInList, T extends TextBlockItem>(props: FieldsWithTextBlocksProps<F, T>) {
  const {
    fieldProps, 
    textBlocksProps,
    disabled,
    onChange,
    combinedItems,
  } = props;

  const renderItem = React.useCallback(
    (
      field:
        | FieldsWithTextBlocksItem<F>
        | FieldsWithTextBlocksItem<T>,
      index: number
    ) => {
      if (field.itemType === FieldsWithTextBlocksItemType.textBlock) {
        return (
          <TextBlock<T>
            shouldScrollBottom={index === combinedItems.length - 1}
            data={field as T}
            showDeleteConfirmMessage={textBlocksProps.showDeleteConfirmMessage}
            onDelete={(field) => handleItemDelete(field as FieldsWithTextBlocksItem<T>)}
            onEdit={textBlocksProps.onEdit}
          />
        );
      }
      return (
        <Field<F>
          data={field as F}
          questionnaireId={fieldProps.questionnaireId}
          pageNumber={fieldProps.pageNumber}
          features={{
            delete: fieldProps.canDeleteField
              ? fieldProps.canDeleteField(field as F)
              : true,
            edit: fieldProps.canEditField
              ? fieldProps.canEditField(field as F)
              : true,
            changeShowStatus: fieldProps.canShowHideField
              ? fieldProps.canShowHideField(field as F)
              : true,
          }}
          showDeleteConfirmMessage={fieldProps.showDeleteConfirmMessage}
          onDelete={(field) => handleItemDelete(field as FieldsWithTextBlocksItem<F>)}
          onEdit={fieldProps.onEdit}
          onShowStatusChange={fieldProps.onShowStatusChange}
        />
      );
    },
    [fieldProps, textBlocksProps]
  );


  const handleGetFieldInListClassName = React.useCallback((item: F) => {
    const { getClassName, hasError } = fieldProps;
    const customClass = getClassName ? getClassName(item) : '';
    const errorClass = hasError && hasError(item) ? 'FhqDesignToolFieldsWithTextBlocks__item--has-error' : '';

    return `FhqDesignToolFieldsWithTextBlocks__item ${customClass} ${errorClass}`;
  }, [fieldProps.getClassName, fieldProps.hasError]);

  const handleGetTextItemClassName = React.useCallback((item: T) => {
    const { getClassName, hasError } = textBlocksProps;
    const customClass = getClassName ? getClassName(item) : '';
    const errorClass = hasError && hasError(item) ? 'FhqDesignToolFieldsWithTextBlocks__item--has-error' : '';

    return `FhqDesignToolFieldsWithTextBlocks__item ${customClass} ${errorClass}`;
  }, [textBlocksProps.getClassName, textBlocksProps.hasError]);

  const draggableFields = React.useMemo(() => {
    if (!fieldProps.canChangeFieldOrder) {
      return combinedItems;
    }

    return combinedItems.filter((field) => fieldProps.canChangeFieldOrder!(field as F));
  }, [combinedItems, fieldProps.canChangeFieldOrder]);

  const nonDraggableFields = React.useMemo(() => {
    if (!fieldProps.canChangeFieldOrder) {
      return [];
    }

    return combinedItems.filter((x) => !fieldProps.canChangeFieldOrder!(x as F));
  }, [combinedItems, fieldProps.canChangeFieldOrder]);

  const handleChange = React.useCallback(
    (updatedItems: Array<FieldsWithTextBlocksItem<T> | FieldsWithTextBlocksItem<F>>) => {
      const itemsWithOrders = updatedItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
      const updatedFields = [...nonDraggableFields, ...itemsWithOrders]
        .filter((item) => item.itemType === FieldsWithTextBlocksItemType.field)
        .map(({ itemType, ...field }) => ({ ...field,  }));
      const updatedTextBlocks = itemsWithOrders
        .filter((item) => item.itemType === FieldsWithTextBlocksItemType.textBlock)
        .map(({ itemType, ...block }) => block);

      onChange({
        texts: updatedTextBlocks as UpdatedLocalTexts<T>,
        fields: updatedFields as UpdatedLocalFields<F>,
      });
    },
    [onChange, nonDraggableFields]
  );

  const handleItemDelete = React.useCallback((deletedItem: FieldsWithTextBlocksItem<T> | FieldsWithTextBlocksItem<F>) => {
    const updatedField = combinedItems
    .filter(x => fieldProps.canChangeFieldOrder ? fieldProps.canChangeFieldOrder(x as F) : true)
    .filter(item => !(item.id == deletedItem.id && item.itemType === deletedItem.itemType));

    handleChange(updatedField)
  }, [combinedItems, handleChange]);

  return (
    <>
      {nonDraggableFields.length > 0 && (
        <div
          className={cx("FhqDesignToolFieldsWithTextBlocks", {
            "FhqDesignToolFieldsWithTextBlocks--disabled": disabled,
          })}
        >
          {nonDraggableFields.map((x, index) => (
            <div key={x.id} className={handleGetFieldInListClassName(x as F)}>
              {renderItem(x, index)}
            </div>
          ))}
        </div>
      )}
      {draggableFields.length > 0 && (
        <DraggableList
          classes={{
            item: (item) =>
              item.itemType === FieldsWithTextBlocksItemType.textBlock
                ? handleGetTextItemClassName(item as T)
                : handleGetFieldInListClassName(item as F),
          }}
          items={draggableFields}
          className={cx("FhqDesignToolFieldsWithTextBlocks", {
            "FhqDesignToolFieldsWithTextBlocks--disabled": disabled,
          })}
          renderItem={renderItem}
          onChange={handleChange as DraggableListProps["onChange"]}
        />
      )}
    </>
  );
}
